import { useState, useMemo, useEffect } from "react";
import { type SortingState } from "@tanstack/react-table";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useExceedances, type ExceedanceFilters } from "@/hooks/use-exceedances";
import { useComplianceSummary } from "@/hooks/use-compliance-summary";
import { useFacilities, useFacility } from "@/hooks/use-facilities";
import { useParameters } from "@/hooks/use-parameters";
import { useExceedanceColumns } from "@/components/sampling/exceedance-columns";
import { ExceedanceFilterControls } from "@/components/sampling/exceedance-filters";
import {
  PageHeader,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  StatCard,
  StatCardSkeleton,
} from "@/components/ui";

export const Route = createFileRoute("/_authenticated/exceedances/")({
  component: ExceedancesPage,
});

const PAGE_SIZE = 20;

function ExceedancesPage() {
  const { t } = useTranslation(["exceedances", "common"]);
  const orgId = useAppStore((s) => s.activeOrgId);

  // Filters
  const [page, setPage] = useState(1);
  const [facilityFilter, setFacilityFilter] = useState("");
  const [facilitySearch, setFacilitySearch] = useState("");
  const [parameterFilter, setParameterFilter] = useState("");
  const [parameterSearch, setParameterSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [acknowledgedFilter, setAcknowledgedFilter] = useState("");
  const [trcFilter, setTrcFilter] = useState("");
  // Warning filter is disabled: period warnings are ephemeral detection-engine
  // output on passing results and are NOT persisted as exceedance records.
  // The exceedance list endpoint cannot filter or count them. When a dedicated
  // warnings endpoint or flag aggregation is added, re-enable this.
  const warningFilter = "";
  const [sorting, setSorting] = useState<SortingState>([{ id: "detected_at", desc: true }]);

  const firstSort = sorting[0];
  const sortBy = firstSort?.id as
    | "detected_at"
    | "facility_name"
    | "parameter_name"
    | "exceedance_value"
    | "exceedance_percentage"
    | undefined;
  const sortDir = firstSort ? (firstSort.desc ? ("desc" as const) : ("asc" as const)) : undefined;

  useEffect(() => {
    setPage(1);
  }, [sorting]);

  // Lookups
  const { data: facilitiesData, isFetching: facilitiesFetching } = useFacilities(orgId, {
    limit: 20,
    status: "active",
    search: facilitySearch || undefined,
  });
  const facilities = facilitiesData?.data ?? [];
  const { data: selectedFacility } = useFacility(orgId, facilityFilter);
  const selectedFacilityLabel = facilityFilter
    ? (facilities.find((f) => f.id === facilityFilter)?.name ?? selectedFacility?.name)
    : undefined;

  const { data: parametersData, isFetching: parametersFetching } = useParameters({
    search: parameterSearch || undefined,
    limit: 20,
  });
  const parameters = parametersData?.parameters ?? [];
  const selectedParameterLabel = parameterFilter
    ? parameters.find((p) => p.id === parameterFilter)?.name
    : undefined;

  // Build query params
  const queryParams = useMemo((): ExceedanceFilters => {
    const params: ExceedanceFilters = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (facilityFilter) params.facility_id = facilityFilter;
    if (parameterFilter) params.parameter_id = parameterFilter;
    if (acknowledgedFilter === "true") params.acknowledged = true;
    if (acknowledgedFilter === "false") params.acknowledged = false;
    if (trcFilter === "true") params.trc_exceeded = true;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    params.sort = sortBy;
    params.order = sortDir;
    return params;
  }, [
    page,
    facilityFilter,
    parameterFilter,
    acknowledgedFilter,
    trcFilter,
    dateFrom,
    dateTo,
    sortBy,
    sortDir,
  ]);

  const { data, isLoading } = useExceedances(orgId, queryParams);
  const exceedances = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Compliance summary for stat cards
  const { data: summaryData, isLoading: summaryLoading } = useComplianceSummary(orgId);

  const stats = useMemo(() => {
    let totalExceedances30d = 0;
    let unacknowledged = 0;
    let trcCount = 0;
    for (const s of summaryData?.data ?? []) {
      totalExceedances30d += s.exceedance_count_30d;
      unacknowledged += s.unacknowledged_count;
      trcCount += s.trc_count_6mo;
    }
    return { totalExceedances30d, unacknowledged, trcCount };
  }, [summaryData]);

  const columns = useExceedanceColumns(t);

  return (
    <div className="space-y-6 p-8">
      <PageHeader title={t("dashboard")} subtitle={t("title")} />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {summaryLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <StatCard
              title={t("totalExceedances30d", { defaultValue: "Exceedances (30d)" })}
              value={stats.totalExceedances30d}
            />
            <StatCard
              title={t("unacknowledged", { ns: "common", defaultValue: "Unacknowledged" })}
              value={stats.unacknowledged}
            />
            <StatCard
              title={t("trcExceeded")}
              value={stats.trcCount}
              subtitle={t("lookbackPeriod", { defaultValue: "Last 6 months" })}
            />
            <StatCard
              title={t("acknowledge")}
              value={stats.totalExceedances30d - stats.unacknowledged}
            />
            <StatCard
              title={t("warnings.periodWarnings", { defaultValue: "Period Warnings" })}
              value={t("common:notAvailableShort", { defaultValue: "N/A" })}
              subtitle={t("warningsPendingBackend", {
                defaultValue: "Requires aggregation endpoint",
              })}
            />
          </>
        )}
      </div>

      {/* Filters */}
      <ExceedanceFilterControls
        facilityFilter={facilityFilter}
        onFacilityChange={(v) => {
          setFacilityFilter(v);
          setPage(1);
        }}
        onFacilitySearch={setFacilitySearch}
        facilities={facilities}
        facilitiesFetching={facilitiesFetching}
        selectedFacilityLabel={selectedFacilityLabel}
        parameterFilter={parameterFilter}
        onParameterChange={(v) => {
          setParameterFilter(v);
          setPage(1);
        }}
        onParameterSearch={setParameterSearch}
        parameters={parameters}
        parametersFetching={parametersFetching}
        selectedParameterLabel={selectedParameterLabel}
        dateFrom={dateFrom}
        onDateFromChange={(v) => {
          setDateFrom(v);
          setPage(1);
        }}
        dateTo={dateTo}
        onDateToChange={(v) => {
          setDateTo(v);
          setPage(1);
        }}
        acknowledgedFilter={acknowledgedFilter}
        onAcknowledgedChange={(v) => {
          setAcknowledgedFilter(v);
          setPage(1);
        }}
        trcFilter={trcFilter}
        onTrcChange={(v) => {
          setTrcFilter(v);
          setPage(1);
        }}
        unacknowledgedCount={stats.unacknowledged}
        warningFilter={warningFilter}
      />

      {/* Table */}
      {isLoading ? (
        <SkeletonTable columns={10} />
      ) : exceedances.length === 0 ? (
        <EmptyState
          title={t("noExceedances", { defaultValue: "No exceedances detected" })}
          description={t("noExceedancesDetail", {
            defaultValue: "All monitored parameters are within permit limits.",
          })}
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={exceedances}
            sorting={sorting}
            onSortingChange={setSorting}
          />
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={total}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
